// "Take from market": the Apps Setup wizard opens this to pull a snapshot's
// keywords, phrases and the winning screenshot sequence into its brand pack.
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBriefPack, listRuns, listScopes } from '@/api/aso';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';

export interface MarketPick {
  /** Comma-separated, ready for the keywords field. */
  keywords: string;
  /** A short market note for the brief. */
  brief: string;
}

const fmt = (sec: number) => new Date(sec * 1000).toLocaleDateString();

export function MarketPicker({ onPick, onClose }: { onPick: (pick: MarketPick) => void; onClose: () => void }) {
  const scopesQ = useQuery({ queryKey: ['aso-scopes'], queryFn: listScopes });
  const scopes = scopesQ.data ?? [];
  const [scopeId, setScopeId] = useState<number>(0);
  useEffect(() => { if (!scopeId && scopes[0]) setScopeId(scopes[0].id); }, [scopes, scopeId]);
  const runsQ = useQuery({ queryKey: ['aso-runs', scopeId], queryFn: () => listRuns(scopeId), enabled: scopeId > 0 });
  const runs = useMemo(() => (runsQ.data ?? []).filter((r) => r.analysis === 'done' || r.keywords === 'done'), [runsQ.data]);
  const [runId, setRunId] = useState<number>(0);
  useEffect(() => { setRunId(runs[0]?.id ?? 0); }, [runs]);
  const packQ = useQuery({ queryKey: ['aso-brief-pack', runId], queryFn: () => getBriefPack(runId), enabled: runId > 0 });
  const pack = packQ.data;

  const [kw, setKw] = useState<Set<string>>(new Set());
  const [phr, setPhr] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!pack) return;
    setKw(new Set(pack.keywords.slice(0, 12).map((k) => k.keyword)));
    setPhr(new Set([...pack.title_phrases.slice(0, 5), ...pack.short_phrases.slice(0, 5)].map((p) => p.phrase)));
  }, [pack]);
  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, v: string) => {
    const next = new Set(set); if (next.has(v)) next.delete(v); else next.add(v); setter(next);
  };

  const hooksLabel = (slugs: string[]) => slugs.map((s) => (s === 'none' ? '—' : pack?.labels.hook?.[s] ?? s)).join(' → ');

  const apply = () => {
    if (!pack) return;
    const scope = scopes.find((s) => s.id === scopeId);
    const keywords = [...kw, ...phr].filter((v, i, a) => a.indexOf(v) === i).join(', ');
    const lines = [`Market reference (${scope?.name ?? 'snapshot'}, ${pack.keywords.length ? 'keywords + ' : ''}patterns from ${fmt(runs.find((r) => r.id === runId)?.started_at ?? 0)}):`];
    if (pack.sequence) lines.push(`- Most common screenshot story: ${hooksLabel(pack.sequence.hooks)} (${pack.sequence.apps_count}/${pack.sequence.apps_total} apps).`);
    if (pack.screenshot_hooks.length) lines.push(`- Screenshot hooks the market leans on: ${pack.screenshot_hooks.map((h) => `${pack.labels.hook?.[h.slug] ?? h.slug} ${h.apps_count}/${h.apps_total}`).join(', ')}.`);
    if (pack.icon_tags.length) lines.push(`- Icon look in this market: ${pack.icon_tags.map((t) => pack.labels.tag?.[t.slug] ?? t.slug).join(', ')}.`);
    if (pack.title_phrases.length) lines.push(`- Title words competitors share: ${pack.title_phrases.slice(0, 6).map((p) => p.phrase).join(', ')}.`);
    onPick({ keywords, brief: lines.join('\n') });
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} title="Take from market" size="3xl">
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={String(scopeId || '')} options={scopes.map((s) => ({ value: String(s.id), label: s.name }))} onChange={(v) => setScopeId(Number(v))} placeholder="Scope" className="sm:w-72" searchable />
          <Select value={String(runId || '')} options={runs.map((r) => ({ value: String(r.id), label: `${fmt(r.started_at)} · ${r.status}${r.analysis === 'done' ? ' · analysed' : ''}${r.keywords === 'done' ? ' · keywords' : ''}` }))}
            onChange={(v) => setRunId(Number(v))} placeholder="Analysed snapshot" className="sm:w-80" />
        </div>
        {scopeId > 0 && runs.length === 0 && !runsQ.isLoading && <p className="text-sm text-content-subtle">This scope has no analysed snapshot yet — run and analyse it on the ASO Intelligence page.</p>}
        {pack && (
          <>
            <div>
              <div className="label mb-1">Keywords {pack.keywords.length === 0 && <span className="text-content-subtle font-normal">(no provider data for this snapshot)</span>}</div>
              <div className="flex flex-wrap gap-1">
                {pack.keywords.map((k) => (
                  <button key={k.keyword} onClick={() => toggle(kw, setKw, k.keyword)}
                    className={`px-2 py-0.5 rounded-full text-xs border ${kw.has(k.keyword) ? 'bg-primary text-white border-primary' : 'border-border text-content-muted'}`}
                    title={`traffic ${k.traffic ?? '—'} · ${k.apps_count} apps rank`}>
                    {k.keyword}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="label mb-1">Phrases competitors share (title · short)</div>
              <div className="flex flex-wrap gap-1">
                {[...pack.title_phrases, ...pack.short_phrases].filter((p, i, a) => a.findIndex((x) => x.phrase === p.phrase) === i).map((p) => (
                  <button key={p.phrase} onClick={() => toggle(phr, setPhr, p.phrase)}
                    className={`px-2 py-0.5 rounded-full text-xs border ${phr.has(p.phrase) ? 'bg-primary text-white border-primary' : 'border-border text-content-muted'}`}
                    title={`${p.apps_count}/${p.apps_total} apps`}>
                    {p.phrase}
                  </button>
                ))}
              </div>
            </div>
            <div className="text-xs text-content-muted space-y-1">
              {pack.sequence && <div><span className="text-content-subtle">Screenshot story:</span> {hooksLabel(pack.sequence.hooks)} <span className="text-content-subtle">({pack.sequence.apps_count}/{pack.sequence.apps_total})</span></div>}
              {pack.icon_tags.length > 0 && <div><span className="text-content-subtle">Icon look:</span> {pack.icon_tags.map((t) => pack.labels.tag?.[t.slug] ?? t.slug).join(', ')}</div>}
            </div>
            <p className="text-[11px] text-content-subtle">Selected keywords and phrases go into the Keywords field; the story, hooks and icon look are appended to the brief as a market note.</p>
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!pack} onClick={apply}>Use in brand pack</Button>
        </div>
      </div>
    </Modal>
  );
}
